// Cave84 — CellarTransfers service (Phase 1 Week 4)
// Append-only audit log of every inter-cellar bottle movement (F1.6).
// This collection is write-once from the app; reads are for history display only.

import {
  addDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  where
} from 'firebase/firestore';
import type { CellarTransfer } from '@cave84/shared-types';
import { typedCollection } from './firestore';

const COLLECTION = 'cellar_transfers';

function col() {
  return typedCollection<CellarTransfer>(COLLECTION);
}

// ---------------------------------------------------------------------------
// Input type — caller supplies everything except id and server timestamp
// ---------------------------------------------------------------------------

export type TransferCreateInput = Omit<CellarTransfer, 'id' | 'transfer_date'> & {
  transfer_date: Date; // converted to serverTimestamp() internally
};

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/**
 * Log a new transfer event. Called exclusively from bottles.ts#transferBottle.
 * Returns the new document ID.
 */
export async function createTransfer(input: TransferCreateInput): Promise<string> {
  const payload: Omit<CellarTransfer, 'id'> = {
    bottle_id: input.bottle_id,
    from_cellar_id: input.from_cellar_id,
    from_location_id: input.from_location_id,
    to_cellar_id: input.to_cellar_id,
    to_location_id: input.to_location_id,
    transfer_date: serverTimestamp() as CellarTransfer['transfer_date'],
    reason: input.reason,
  };
  const ref = await addDoc(col(), payload as unknown as CellarTransfer);
  return ref.id;
}

// ---------------------------------------------------------------------------
// Reads (history display)
// ---------------------------------------------------------------------------

/**
 * All transfers for a given bottle, newest-first.
 */
export async function listTransfersByBottle(bottleId: string): Promise<CellarTransfer[]> {
  const snap = await getDocs(
    query(col(), where('bottle_id', '==', bottleId), orderBy('transfer_date', 'desc'))
  );
  return snap.docs.map((d) => d.data());
}

/**
 * All transfers out of a given cellar (for audit / history view).
 */
export async function listTransfersByCellar(cellarId: string): Promise<CellarTransfer[]> {
  const snap = await getDocs(
    query(col(), where('from_cellar_id', '==', cellarId), orderBy('transfer_date', 'desc'))
  );
  return snap.docs.map((d) => d.data());
}
